import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { AdminHeader, AField } from "../../components/AdminLayout";
import ImageDropzone from "../../components/ImageDropzone";
import ColorPicker from "../../components/ColorPicker";

const DEVICE_GROUPS = [
  { group: "Android", items: [
    { key: "a1", label: "360 × 800  ⭐", w: 360, h: 800 },
    { key: "a2", label: "360 × 780", w: 360, h: 780 },
    { key: "a3", label: "393 × 852", w: 393, h: 852 },
    { key: "a4", label: "412 × 915", w: 412, h: 915 },
    { key: "a5", label: "412 × 892", w: 412, h: 892 },
  ] },
  { group: "iPhone", items: [
    { key: "i1", label: "iPhone 15 / 16 / 17 — 393 × 852", w: 393, h: 852 },
    { key: "i2", label: "iPhone Pro Max — 430 × 932", w: 430, h: 932 },
  ] },
  { group: "Desktop", items: [
    { key: "d1", label: "Computer — 1280 × 800", w: 1280, h: 800 },
  ] },
];
const ALL_DEVICES = DEVICE_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.group })));

export default function AdminDesign() {
  const [s, setS] = useState(null);
  const [deviceKey, setDeviceKey] = useState("a1");
  const [custom, setCustom] = useState(false);
  const [cw, setCw] = useState(390);
  const [ch, setCh] = useState(844);
  const [busy, setBusy] = useState(false);
  const loadSettings = useCallback(() => { api.get("/site-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  if (!s) return <p className="text-[#A19DB0] text-sm">Loading…</p>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const dev = ALL_DEVICES.find((d) => d.key === deviceKey) || ALL_DEVICES[0];
  const pw = custom ? Math.max(200, Math.min(2000, Number(cw) || 360)) : dev.w;
  const ph = custom ? Math.max(200, Math.min(2000, Number(ch) || 800)) : dev.h;
  const isDesktop = !custom && dev.group === "Desktop";
  const scale = Math.min(340 / pw, 600 / ph);

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/site-settings", s); toast.success("Login page design saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <AdminHeader title="Login page design" subtitle="Customise how the login/sign-up screen looks" />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl items-start">
        <div className="space-y-4">
          <AField label="Headline" value={s.headline} onChange={(e) => set("headline", e.target.value)} data-testid="design-headline" />
          <AField label="Tagline" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="design-tagline" />
          <div><p className="text-xs text-[#A19DB0] mb-1">Logo image <span className="text-[#A19DB0]/60">· shown as a rounded tile on the login screen</span></p><ImageDropzone value={s.logo_url} onUploaded={(u) => set("logo_url", u)} onClear={() => set("logo_url", "")} endpoint="/admin/upload-image" resultKey="url" label="Upload logo" previewClassName="h-28 w-28 mx-auto object-contain rounded-xl bg-white/5 p-2" /></div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
            <p className="text-xs text-[#A19DB0]">Background image <span className="text-[#A19DB0]/60">· fills the full login screen</span></p>
            <ImageDropzone value={s.background_url} onUploaded={(u) => set("background_url", u)} onClear={() => set("background_url", "")} endpoint="/admin/upload-image" resultKey="url" label="Upload background" previewClassName="w-40 aspect-[9/16] mx-auto object-cover rounded-lg" />
            <div className="text-[11px] text-[#A19DB0] leading-relaxed bg-black/20 rounded-lg p-2.5" data-testid="bg-guidance">
              <p className="font-medium text-[#E8E4F0] mb-1">Recommended size</p>
              <p>• Portrait <b>1080 × 1920 px</b> (9:16) looks best · minimum 800 × 1400 px</p>
              <p>• JPG or PNG, up to ~5 MB · keep the lower third simple (text sits there)</p>
            </div>
            <div>
              <p className="text-[11px] text-[#A19DB0] mb-1">Fit</p>
              <div className="grid grid-cols-2 gap-2">
                {[["cover", "Fill (cover)"], ["contain", "Fit whole image"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set("bg_fit", v)} data-testid={`bg-fit-${v}`} className={`rounded-full py-1.5 text-xs font-medium border transition-colors ${((s.bg_fit || "cover") === v) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{l}</button>
                ))}
              </div>
              <button type="button" onClick={() => { set("bg_fit", "cover"); set("bg_scale", 100); set("bg_position", "center"); }} data-testid="bg-fill-perfect" className="mt-2 w-full rounded-full py-1.5 text-xs font-semibold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30 active:scale-[0.98] transition-transform">Fill screen perfectly</button>
            </div>
            <div>
              <p className="text-[11px] text-[#A19DB0] mb-1">Position</p>
              <div className="grid grid-cols-3 gap-2">
                {["top", "center", "bottom"].map((v) => (
                  <button key={v} type="button" onClick={() => set("bg_position", v)} data-testid={`bg-pos-${v}`} className={`rounded-full py-1.5 text-xs font-medium border capitalize transition-colors ${((s.bg_position || "center") === v) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{v}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] text-[#A19DB0] mb-1">Size / zoom · {s.bg_scale ?? 100}% <span className="text-[#A19DB0]/60">(100% = use Fit setting)</span></p>
              <input type="range" min="50" max="250" step="5" value={s.bg_scale ?? 100} onChange={(e) => set("bg_scale", Number(e.target.value))} data-testid="bg-scale" className="w-full accent-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[11px] text-[#A19DB0] mb-1">Darken for text readability · {s.bg_dim ?? 55}%</p>
              <input type="range" min="0" max="90" value={s.bg_dim ?? 55} onChange={(e) => set("bg_dim", Number(e.target.value))} data-testid="bg-dim" className="w-full accent-[#D4AF37]" />
            </div>
          </div>
          <div><p className="text-xs text-[#A19DB0] mb-1">Primary (accent) colour</p><ColorPicker value={s.primary_color} onChange={(v) => set("primary_color", v)} /></div>
          <div><p className="text-xs text-[#A19DB0] mb-1">Secondary colour</p><ColorPicker value={s.accent_color} onChange={(v) => set("accent_color", v)} /></div>
          <button onClick={save} disabled={busy} data-testid="save-design" className="rounded-full bg-[#D4AF37] text-black font-semibold px-6 py-3 text-sm active:scale-95 transition-transform disabled:opacity-60">{busy ? "Saving…" : "Save design"}</button>
        </div>
        <div className="lg:sticky lg:top-6 self-start order-first lg:order-none">
          <div className="mb-3 space-y-2">
            <p className="text-xs text-[#A19DB0]">Live preview <span className="text-[#A19DB0]/60">· updates as you edit · {pw} × {ph} px</span></p>
            <select value={custom ? "custom" : deviceKey} data-testid="preview-device-select"
              onChange={(e) => { if (e.target.value === "custom") { setCustom(true); } else { setCustom(false); setDeviceKey(e.target.value); } }}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]">
              {DEVICE_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.group} className="bg-[#140F1E]">
                  {g.items.map((it) => <option key={it.key} value={it.key} className="bg-[#140F1E]">{it.label}</option>)}
                </optgroup>
              ))}
              <optgroup label="Custom" className="bg-[#140F1E]"><option value="custom" className="bg-[#140F1E]">Custom size…</option></optgroup>
            </select>
            {custom && (
              <div className="flex items-center gap-2">
                <input type="number" value={cw} onChange={(e) => setCw(e.target.value)} data-testid="custom-width" placeholder="Width" className="w-24 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                <span className="text-[#A19DB0]">×</span>
                <input type="number" value={ch} onChange={(e) => setCh(e.target.value)} data-testid="custom-height" placeholder="Height" className="w-24 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                <span className="text-xs text-[#A19DB0]">px</span>
              </div>
            )}
          </div>
          <div style={{ width: pw * scale, height: ph * scale }} className="mx-auto transition-all duration-300">
            <div data-testid="design-preview" style={{ width: pw, height: ph, transform: `scale(${scale})`, transformOrigin: "top left" }}
              className={`overflow-hidden border-white/15 relative flex flex-col justify-end bg-[#0D0B14] ${isDesktop ? "rounded-xl border" : "rounded-[2.4rem] border-[6px]"}`}>
              {s.background_url && <div className="absolute inset-0" style={{ backgroundImage: `url(${s.background_url})`, backgroundSize: (s.bg_scale && s.bg_scale !== 100) ? `${s.bg_scale}%` : (s.bg_fit || "cover"), backgroundPosition: s.bg_position || "center", backgroundRepeat: "no-repeat", backgroundColor: "#0D0B14" }} />}
              <div className="absolute inset-0 bg-[#0D0B14]" style={{ opacity: (s.bg_dim ?? 55) / 100 }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B14] via-[#0D0B14]/70 to-transparent" />
              <div className={`relative z-10 px-7 pb-10 pt-16 ${isDesktop ? "max-w-md w-full mx-auto" : ""}`}>
                {s.logo_url ? <img src={s.logo_url} alt="logo" className="w-20 h-20 rounded-2xl object-cover mb-4" /> : <Palette size={26} style={{ color: s.primary_color }} className="mb-4" />}
                <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: s.primary_color }}>Darkchata</p>
                <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">{s.headline}</h1>
                <p className="text-[#A19DB0] mt-2 text-sm">{s.tagline}</p>
                <div className="mt-7 rounded-2xl bg-white/[0.04] backdrop-blur-2xl border border-[#D4AF37]/15 ring-1 ring-white/5 p-5 space-y-5">
                  <div className="h-8 border-b border-white/20" />
                  <div className="h-8 border-b border-white/20" />
                  <div className="rounded-full h-11 w-full" style={{ background: s.primary_color }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
